import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Hourglass, AlertCircle } from "lucide-react";
import {
  bulkExtendTrial,
  type BulkOperationResultView,
} from "../../../api/General/Subscription";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyIds: number[];
  onSuccess: (result: BulkOperationResultView) => void;
}

export default function BulkExtendTrialDialog({
  open,
  onOpenChange,
  companyIds,
  onSuccess,
}: Props) {
  const { t } = useTranslation();
  const [days, setDays] = useState<number>(14);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setDays(14);
      setNotes("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  const count = companyIds.length;

  const submit = async () => {
    if (count === 0) return;
    if (!Number.isFinite(days) || days <= 0) {
      setError(t("Additional Days"));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await bulkExtendTrial({
        companyIds,
        additionalDays: days,
        notes: notes.trim() || null,
      });
      if (result.errors && result.errors.length > 0) {
        setError(result.errors[0]?.message || t("Failed to update subscription"));
        setSubmitting(false);
        return;
      }
      if (result.payload) {
        onSuccess(result.payload);
      }
      onOpenChange(false);
    } catch (e) {
      const message =
        (e as { response?: { data?: { errors?: { message?: string }[] } } })?.response?.data
          ?.errors?.[0]?.message || t("Failed to update subscription");
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 flex items-center justify-center">
              <Hourglass size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{t("Extend Trial")}</DialogTitle>
              <DialogDescription className="mt-1">
                {t("{{count}} companies selected", { count })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-900/50 p-3 text-sm text-amber-800 dark:text-amber-300">
            {t("This will extend the trial for {{count}} companies by the chosen number of days. Companies not on a trial will be skipped.", {
              count,
            })}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="bet-days">
              {t("Additional Days")}
            </label>
            <Input
              id="bet-days"
              type="number"
              min={1}
              max={365}
              value={days}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setDays(Math.max(1, parseInt(e.target.value || "0", 10) || 0))
              }
            />
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="bet-notes">
              {t("Notes (optional)")}
            </label>
            <textarea
              id="bet-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t("Add a note about this change")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors disabled:opacity-50"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button onClick={submit} loading={submitting} variant="warning" disabled={count === 0}>
            {t("Extend Trial for {{count}}", { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
